import IonToolbar from '../../../../../components/Modal/ModalToolbar';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonLoading, IonPage, IonRefresher, IonRefresherContent, IonTitle, IonToast, useIonViewDidEnter } from '@ionic/react';
import { arrowBack, checkmarkCircleOutline, chevronForwardOutline, closeCircleOutline, documentTextOutline, receiptOutline, removeCircleOutline } from 'ionicons/icons';
import React, { useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MessageContext } from '../../../../../contexts/MessageContext';
import { fiscalListarNFCe, requestService } from '../../../../../Utils/Services';
import GridStatus from '../../../../../components/Grid/GridStatus';
import '../FiscalLayout.css';
import './FiscalHistorico.css';

type Props = {
    abrirMenu: boolean;
};

const FiscalHistorico: React.FC<Props> = (props: Props) => {
    const history = useHistory();
    const { loja } = useContext(MessageContext);
    const { esconderMenu, setEsconderMenu } = useContext(MessageContext);
    const { nomeMenu, setNomeMenu } = useContext(MessageContext);

    const [notas, setNotas] = useState<any[]>([]);
    const [showLoading, setShowLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [textToast, setTextToast] = useState("");
    const [searchText, setSearchText] = useState("");
    const [lastKey, setLastKey] = useState<any>(null);
    const [hasMore, setHasMore] = useState(true);

    // Ref para evitar stale closure ao ler lastKey dentro de carregarNotas
    const lastKeyRef = useRef<any>(null);

    const [acessos] = useState<any>(JSON.parse(window.localStorage.getItem("acessos") || '{}'));

    useIonViewDidEnter(() => {
        if (acessos?.fiscal != 'leitura' && acessos?.fiscal != 'edicao') {
            history.replace('/empresa/home');
            return;
        }
        setEsconderMenu(true);
        setNomeMenu("");
        carregarNotas(true);
    });

    function carregarNotas(reset: boolean = false) {
        if (!loja) return;

        if (reset) {
            setShowLoading(true);
            setNotas([]);
            setLastKey(null);
            lastKeyRef.current = null;
        }

        let url = fiscalListarNFCe.url.replace("{empresaId}", loja.replaceAll(/#/g, '%23'));
        url += "?limite=50";
        if (!reset && lastKeyRef.current) {
            url += "&lastKey=" + encodeURIComponent(JSON.stringify(lastKeyRef.current));
        }

        requestService(
            url,
            { method: fiscalListarNFCe.method, credentials: 'include' },
            history,
            (response: any) => {
                setShowLoading(false);
                const itemsRaw = response.dados ?? response.items ?? response.notas;
                const items = Array.isArray(itemsRaw) ? itemsRaw : [];
                if (reset) {
                    setNotas(items);
                } else {
                    setNotas(prev => [...prev, ...items]);
                }
                lastKeyRef.current = response.lastKey || null;
                setLastKey(response.lastKey || null);
                setHasMore(!!response.lastKey);
            },
            (error: any) => {
                setShowLoading(false);
                setTextToast(error.message || "Erro ao carregar notas.");
                setShowToast(true);
            }
        );
    }

    function doRefresh(event: any) {
        carregarNotas(true);
        setTimeout(() => event.detail.complete(), 500);
    }

    function loadMore(event: any) {
        if (!hasMore || !lastKeyRef.current) {
            event.target.complete();
            return;
        }
        carregarNotas(false);
        // complete() chamado após carregamento dentro de carregarNotas via setHasMore
        // mas o IonInfiniteScroll precisa do complete() para sair do estado de loading
        setTimeout(() => event.target.complete(), 1500);
    }

    function formatarData(data: string) {
        if (!data) return "";
        return new Date(data).toLocaleDateString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }

    function formatarValor(valor: number) {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    }

    function getStatusClass(status: string) {
        if (!status) return "";
        const s = status.toUpperCase();
        if (s.includes("AUTORIZADA")) return "autorizada";
        if (s.includes("CANCELADA")) return "cancelada";
        if (s.includes("INUTILIZADA")) return "inutilizada";
        return "";
    }

    function abrirDetalhes(nota: any) {
        history.push('/empresa/fiscaldetalhes', { nota });
    }

    const notasFiltradas = Array.isArray(notas) ? notas.filter(nota => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
            (nota.numero && String(nota.numero).includes(search)) ||
            (nota.chave && nota.chave.includes(search)) ||
            (nota.status && nota.status.toLowerCase().includes(search))
        );
    }) : [];

    return (
        <IonPage className="fiscal-layout fiscal-historico-page">
            <IonHeader className="ion-no-border">
                <IonToolbar className="fiscal-layout-toolbar">
                    <IonButtons slot="start">
                        <IonBackButton icon={arrowBack} text='' />
                    </IonButtons>
                    <IonTitle slot="start">Histórico de Notas</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent>
                <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
                    <IonRefresherContent />
                </IonRefresher>

                <div className="fiscal-layout-container fiscal-historico-container">
                    <div className="fiscal-layout-heading">
                        <h1>Histórico de notas fiscais</h1>
                        <p>Acompanhe as notas emitidas e selecione uma linha para consultar os detalhes.</p>
                    </div>

                    <section className="fiscal-historico-grid" aria-labelledby="fiscal-historico-grid-title">
                        <div className="fiscal-historico-grid-heading">
                            <span className="fiscal-historico-icon"><IonIcon icon={receiptOutline} aria-hidden="true" /></span>
                            <div>
                                <h2 id="fiscal-historico-grid-title">Notas fiscais</h2>
                                <p>{notasFiltradas.length} {notasFiltradas.length === 1 ? 'nota carregada' : 'notas carregadas'}</p>
                            </div>
                        </div>
                        {notasFiltradas.length === 0 && !showLoading ? (
                            <div className="fiscal-historico-empty">
                                <span className="fiscal-historico-icon"><IonIcon icon={documentTextOutline} aria-hidden="true" /></span>
                                <h3>Nenhuma nota encontrada</h3>
                                <p>As notas emitidas aparecerão aqui.</p>
                            </div>
                        ) : (
                            <table className="fiscal-historico-table" aria-label="Notas fiscais emitidas">
                                <thead>
                                    <tr>
                                        <th scope="col">Nota fiscal</th>
                                        <th scope="col">Emissão</th>
                                        <th scope="col">Status</th>
                                        <th scope="col" className="fiscal-historico-value">Valor</th>
                                        <th scope="col"><span className="fiscal-historico-sr-only">Detalhes</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notasFiltradas.map((nota: any, index: number) => {
                                        const statusClass = getStatusClass(nota.status);
                                        return (
                                            <tr key={nota.id || nota.nfceId || index}
                                                tabIndex={0}
                                                aria-label={`Consultar detalhes da NFC-e nº ${nota.numero || '—'}`}
                                                onClick={() => abrirDetalhes(nota)}
                                                onKeyDown={(event) => {
                                                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                                        event.preventDefault();
                                                        abrirDetalhes(nota);
                                                    }
                                                }}>
                                                <td className="fiscal-historico-number">NFC-e nº {nota.numero || '—'}</td>
                                                <td className="fiscal-historico-date">{formatarData(nota.dataEmissao || nota.createdAt)}</td>
                                                <td className="fiscal-historico-status">
                                                    <GridStatus tone={statusClass === 'autorizada' ? 'success' : 'neutral'}
                                                        icon={statusClass === 'autorizada' ? checkmarkCircleOutline : statusClass === 'cancelada' ? closeCircleOutline : removeCircleOutline}>
                                                        {nota.status || '—'}
                                                    </GridStatus>
                                                </td>
                                                <td className="fiscal-historico-value">{formatarValor(nota.valorTotal)}</td>
                                                <td className="fiscal-historico-chevron"><IonIcon icon={chevronForwardOutline} aria-hidden="true" /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </section>
                </div>

                <IonInfiniteScroll threshold="100px" disabled={!hasMore} onIonInfinite={loadMore}>
                    <IonInfiniteScrollContent loadingText="Carregando mais notas..." />
                </IonInfiniteScroll>

                <IonLoading isOpen={showLoading} message="Carregando notas..." />

                <IonToast
                    isOpen={showToast}
                    position="top"
                    onDidDismiss={() => setShowToast(false)}
                    message={textToast}
                    duration={3000}
                    color="danger"
                />
            </IonContent>
        </IonPage>
    );
};

export default FiscalHistorico;
